<template>
  <nav class="home-sidebar">
    <NebulaSidebar
      class="home-sidebar__nebula-sidebar"
    >
      <template #sidebar-top>
        <NebulaButton
          class="home-sidebar__create-button"
          :aria-expanded="dropdownOpen.toString()"
          icon-left="plus"
          :text="$t('Create New')"
          @click="toggleDropdown"
        />
        <div class="nebula-sidebar__section">
          <ul class="section__list">
            <li class="section__list-item">
              <NebulaButton
                class="home-sidebar__list-link"
                :class="{
                  'home-sidebar__list-link--active': $route.name === 'home'
                }"
                link="/"
                router-link
                shape="pill"
                size="s"
                type="flat"
                :text="$t('My Studio')"
              />
            </li>
            <li class="section__list-item">
              <NebulaButton
                class="home-sidebar__list-link"
                :class="{
                  'home-sidebar__list-link--active': $route.name === 'shared-with-me'
                }"
                link="/shared-with-me"
                router-link
                shape="pill"
                size="s"
                type="flat"
                :text="$t('Shared with Me')"
              />
            </li>
            <li
              class="section__list-item"
              v-if="!userIsStudent"
            >
              <NebulaButton
                class="home-sidebar__list-link"
                :class="{
                  'home-sidebar__list-link--active': $route.name === 'assigned'
                }"
                link="/creations/assigned"
                router-link
                shape="pill"
                size="s"
                type="flat"
                :text="$t('Assigned')"
              />
            </li>
            <li class="section__list-item">
              <NebulaButton
                class="home-sidebar__list-link"
                :class="{
                  'home-sidebar__list-link--active': $route.name === 'trash'
                }"
                link="/trash"
                router-link
                shape="pill"
                size="s"
                type="flat"
                :text="$t('Trash')"
              />
            </li>
          </ul>
        </div>
      </template>
    </NebulaSidebar>
    <transition
      name="create-dd__menu-transition"
      enter-from-class="create-dd__menu-transition--enter-from"
      enter-active-class="create-dd__menu-transition--enter-active"
      leave-active-class="create-dd__menu-transition--leave-active"
      leave-to-class="create-dd__menu-transition--leave-to"
    >
      <div
        v-show="dropdownOpen"
        ref="createDD"
        :aria-hidden="!dropdownOpen"
        class="create-dd__menu-wrapper"
      >
        <FocusTrap
          :active="dropdownOpen"
          :fallback-focus="() => $refs.dropdownCloseButton.$el"
          :initial-focus="() => $refs.dropdownCloseButton.$el"
          @deactivate="resetDropdowns"
        >
          <div>
            <div class="create-dd__menu-heading-wrapper">
              <h2
                v-if="!templateMenuOpen"
                class="create-dd__menu-heading"
              >
                {{ $t('Create New') }}
              </h2>
              <NebulaButton
                v-else
                ref="backButton"
                aria-expanded="true"
                class="create-dd__back-button"
                icon-left="arrow-left"
                size="l"
                :text="$t('Go Back')"
                type="flat"
                @click="toggleTemplateMenu"
              />
              <NebulaButton
                ref="dropdownCloseButton"
                class="create-dd__close-button"
                :aria-label="$t('Close')"
                icon-left="x"
                size="s"
                type="flat"
                @click="toggleDropdown"
              />
            </div>
            <ul
              class="create-dd__menu"
              role="menu"
            >
              <li
                v-if="userHasActivityTemplates && !userIsStudent && !templateMenuOpen"
                class="create-dd__menu-item"
              >
                <RouterLink
                  class="create-dd__menu-button create-dd__menu-button--activity-templates"
                  :to="{ name: 'activity-templates' }"
                  @click="toggleDropdown"
                >
                  <NebulaIcon
                    class="create-dd__menu-item-icon"
                    symbol-id="studio-template"
                  />
                  <div>
                    <div class="create-dd__menu-item-label">
                      {{ $t('Activity Templates') }}
                    </div>
                    <p class="create-dd__menu-item-description">
                      {{ $t('Browse and create with Activities made by Discovery Education') }}
                    </p>
                  </div>
                </RouterLink>
              </li>
              <li class="create-dd__menu-item">
                <button
                  class="create-dd__menu-button create-dd__menu-button--board"
                  @click="newDraft('board', templateMenuOpen)"
                >
                  <NebulaIcon
                    class="create-dd__menu-item-icon"
                    symbol-id="studio-board"
                  />
                  <div>
                    <div class="create-dd__menu-item-label">
                      {{ $t('Board') }}
                    </div>
                    <p class="create-dd__menu-item-description">
                      <!-- eslint-disable-next-line max-len -->
                      {{ $t('Add as much content as you need to each page – great for projects') }}
                    </p>
                  </div>
                </button>
              </li>
              <li class="create-dd__menu-item">
                <button
                  class="create-dd__menu-button create-dd__menu-button--slides"
                  @click="newDraft('slides', templateMenuOpen)"
                >
                  <NebulaIcon
                    class="create-dd__menu-item-icon"
                    symbol-id="studio-slideshow"
                  />
                  <div>
                    <div class="create-dd__menu-item-label">
                      {{ $t('Slideshow') }}
                    </div>
                    <p class="create-dd__menu-item-description">
                      {{ $t('Fixed amount of space to add content – great for presentations') }}
                    </p>
                  </div>
                </button>
              </li>
              <li
                class="create-dd__menu-item"
                v-if="isDeUser && !templateMenuOpen && userHasLessons"
              >
                <button
                  class="create-dd__menu-button create-dd__menu-button--slides"
                  @click="newDraft('lesson', templateMenuOpen)"
                >
                  <NebulaIcon
                    class="create-dd__menu-item-icon"
                    symbol-id="learning"
                  />
                  <div>
                    <div class="create-dd__menu-item-label">
                      {{ $t('Lesson') }}
                    </div>
                    <p class="create-dd__menu-item-description">
                      {{ $t('Create a Slideshow Lesson to be published in the DE Platform') }}
                    </p>
                  </div>
                </button>
              </li>
              <li
                class="create-dd__menu-item"
                v-if="isDeUser && !templateMenuOpen"
              >
                <button
                  ref="createTemplateButton"
                  class="create-dd__menu-button create-dd__menu-button--slides"
                  :aria-expanded="templateMenuOpen.toString()"
                  @click="toggleTemplateMenu"
                >
                  <NebulaIcon
                    class="create-dd__menu-item-icon"
                    symbol-id="studio-template-create"
                  />
                  <div>
                    <div class="create-dd__menu-item-label">
                      {{ $t('Template') }}
                    </div>
                    <p class="create-dd__menu-item-description">
                      <!-- eslint-disable-next-line max-len -->
                      {{ $t('Add your creation to the Activity Templates Library to be used by customers') }}
                    </p>
                  </div>
                  <NebulaIcon
                    class="create-dd__menu-item-icon create-dd__menu-item-icon--right"
                    symbol-id="caret-right"
                  />
                </button>
              </li>
            </ul>
          </div>
        </FocusTrap>
      </div>
    </transition>
  </nav>
</template>

<script>
import { FocusTrap } from 'focus-trap-vue';
import { get } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import { NebulaButton, NebulaIcon, NebulaSidebar } from '@discoveryedu/nebula-components';
import { PRODUCT_SETTING_STUDIO_LESSONS_ENABLED } from '@/lib/constants';
import * as types from '@/lib/constants/store';
import {
  useAppStore,
  useEditorStore,
  useUserStore,
} from '@/stores';

export default {
  name: 'HomeSidebar',
  components: {
    FocusTrap,
    NebulaButton,
    NebulaIcon,
    NebulaSidebar,
  },
  data() {
    return {
      dropdownOpen: false,
      templateMenuOpen: false,
    };
  },
  computed: {
    ...mapState(useUserStore, [
      'isDeUser',
      'userHasActivityTemplates',
      'userIsStudent',
      'productSettings',
    ]),
    userHasLessons() {
      // Lessons are hidden behind a product setting
      return get(this.productSettings, `${PRODUCT_SETTING_STUDIO_LESSONS_ENABLED}.value`);
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.CREATE_DRAFT,
    ]),
    ...mapActions(useAppStore, [
      types.UPDATE_LOADING,
      types.SET_ALLOW_LOADING_NEXT_ROUTE_CHANGE,
    ]),
    async newDraft(type, isTemplate) {
      const draft = await this[types.CREATE_DRAFT]({
        type,
        isTemplate,
      });

      if (draft) {
        // If we have draft, show loading while we navigate
        this[types.SET_ALLOW_LOADING_NEXT_ROUTE_CHANGE](true);
        this[types.UPDATE_LOADING](true);
        // New drafts always have a single reference
        const draftReference = draft.references[0];
        this.$router.push(`/edit?id=${draftReference.reference_id}`);
      }
    },
    onBodyClick({ target }) {
      if (!this.$refs.createDD.contains(target)) {
        this.resetDropdowns();
      }
    },
    resetDropdowns() {
      this.dropdownOpen = false;

      // Wait for the transition to finish before resetting the interior menu
      setTimeout(() => {
        this.templateMenuOpen = false;
      }, 250);
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
    },
    toggleTemplateMenu() {
      this.templateMenuOpen = !this.templateMenuOpen;

      // Handle focus between the two states
      this.$nextTick(() => {
        if (this.$refs.backButton) this.$refs.backButton.$el.focus();
        else if (this.$refs.createTemplateButton) this.$refs.createTemplateButton.focus();
      });
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.onBodyClick);
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.onBodyClick);
  },
};
</script>

<style lang="stylus">
$navbar-height = 56px;
$sidebar-width = 215px;

.home-sidebar {
  flex-grow: 0;
  flex-shrink: 0;
  height: 100vh;
  position: sticky;
  top: 0;
  z-index: 25; // clear board filters

  &__list-link--active {
    --nebula-button-background: $nebula-color-platform-interactive-200;
    --nebula-button-text-color: $nebula-color-platform-interactive-800;
  }

  .page-content--embedded & {
    height: 100vh;
  }

  &__nebula-sidebar {
    height: 100%;
  }

  &__create-button {
    justify-content: center;
    margin-bottom: $nebula-space-4x;
    width: 100%;
  }
}

.create-dd {
  &__menu-wrapper {
    background-color: $nebula-color-white;
    bottom: 0;
    box-shadow: $nebula-shadow-400;
    left: 0;
    max-width: 100vw;
    overflow-y: auto;
    position: absolute;
    top: 0;
    transform: translateX(0);
    transition: transform $nebula-transition-default;
    width: 400px;
    z-index: 15;
  }

  &__menu-heading-wrapper {
    align-items: center;
    background-color: $nebula-color-white;
    border-bottom: 1px solid $nebula-color-interface-blue-200;
    display: flex;
    margin: 0 $nebula-space-1x $nebula-space-1x;
    padding: $nebula-space-3x $nebula-space-2x $nebula-space-2x;
    position: sticky;
    top: 0;
  }

  &__menu-heading {
    nebula-text-h5();
    flex-grow: 1;
    line-height: $nebula-space-6x; // prevent a jump when this swaps out for the back button
    margin: 0;
    padding-inline-end: $nebula-space-2x;
  }

  &__back-button {
    margin-right: auto;
  }

  &__close-button {
    .nebula-button__icon {
      height: $nebula-space-3x;
      width: $nebula-space-3x;
    }
  }

  &__menu { // .create-dd__menu
    list-style-type: none;
    margin: 0;
    min-width: 100%;
    padding: $nebula-space-1x;
  }

  &__menu-item { // .create-dd__menu-item
    border-radius: $nebula-border-radius-badge-default;

    &:not(:last-child) {
      margin-bottom: $nebula-space-1x;
    }
  }

  &__menu-button { // .create-dd__menu-button
    align-items: center;
    background: none;
    background-color: $nebula-color-white;
    border: 0;
    border-radius: $nebula-border-radius-badge-default;
    display: flex;
    font-size: $nebula-font-size-body-1;
    padding: $nebula-space-2x;
    text-align: left;
    text-decoration: none;
    width: 100%;

    &:hover, &:focus {
      background-color: $nebula-color-interface-blue-100;
    }
  }

  &__menu-item-icon {
    display: block;
    fill: $nebula-color-interface-blue-500;
    flex-shrink: 0;
    height: $nebula-space-4x;
    margin-inline-end: $nebula-space-2x;
    width: $nebula-space-4x;

    &--right {
      height: $nebula-space-3x;
      margin-inline: $nebula-space-2x 0;
      width: $nebula-space-3x;
    }
  }

  &__menu-item-label {
    nebula-text-h6();
    color: $nebula-color-interface-blue-500;
  }

  &__menu-item-description {
    nebula-text-body-1();
    margin: 0;
  }

  &__menu-transition--enter-active,
  &__menu-transition--leave-active {
    transition: transform $nebula-transition-default;
  }

  &__menu-transition--enter-from,
  &__menu-transition--leave-to {
    transform: translateX(-100%);
  }

  html[dir="rtl"] & {
    &__button svg {
      margin-left: $nebula-space-1x;
      margin-right: 0;
    }

    &__menu-wrapper {
      left: auto;
      right: 0;
    }

    &__back-button {
      margin-left: auto;
      margin-right: 0;

      svg {
        transform: scaleX(-1);
      }

      .nebula-button__text {
        margin-inline: $nebula-space-1x 0;
      }
    }

    &__menu-button {
      text-align: right;
    }

    &__menu-item-icon {
      transform: scaleX(-1);
    }

    &__menu-transition--enter-from,
    &__menu-transition--leave-to {
      transform: translateX(100%);
    }
  }
}
</style>
